<script>
	import { onMount, createEventDispatcher } from "svelte";

	export let type = "success"; // "success", "warning", "error", "danger", "info"
	export let variant = ""; // Compatibilidade retroativa
	export let message = "";
	export let dismissible = true;
	export let timeToClose = 4;
	export let onClose = null;
	export let closeAlert = null;

	const dispatch = createEventDispatcher();

	$: activeType = variant || type || "info";
	$: isError = activeType === "error" || activeType === "danger";

	let progress = 100;
	let timer;

	function handleClose() {
		if (timer) clearInterval(timer);
		if (onClose) onClose();
		if (closeAlert && closeAlert !== onClose) closeAlert();
		dispatch("close");
	}

	onMount(() => {
		const totalMs = Math.max(1, timeToClose) * 1000;
		const stepMs = totalMs / 100;
		timer = setInterval(() => {
			if (progress <= 0) {
				clearInterval(timer);
				handleClose();
			} else {
				progress -= 1;
			}
		}, stepMs);

		return () => {
			if (timer) clearInterval(timer);
		};
	});
</script>

<div class="matafome-toast {activeType}" role="alert">
	<div class="toast-indicator"></div>
	<div class="toast-body">
		<div class="toast-icon">
			{#if activeType === "success"}
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<polyline points="20 6 9 17 4 12"/>
				</svg>
			{:else if activeType === "warning"}
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
					<line x1="12" y1="9" x2="12" y2="13"/>
					<line x1="12" y1="17" x2="12.01" y2="17"/>
				</svg>
			{:else if isError}
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="12" cy="12" r="10"/>
					<line x1="15" y1="9" x2="9" y2="15"/>
					<line x1="9" y1="9" x2="15" y2="15"/>
				</svg>
			{:else}
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="12" cy="12" r="10"/>
					<line x1="12" y1="16" x2="12" y2="12"/>
					<line x1="12" y1="8" x2="12.01" y2="8"/>
				</svg>
			{/if}
		</div>

		<div class="toast-content">
			{#if message}
				<div class="toast-msg">{message}</div>
			{/if}
			<slot></slot>
		</div>

		{#if dismissible}
			<button type="button" class="toast-close" on:click={handleClose} title="Fechar notificação">
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"/>
					<line x1="6" y1="6" x2="18" y2="18"/>
				</svg>
			</button>
		{/if}
	</div>

	<div class="toast-progress-track">
		<div class="toast-progress-bar" style="width: {progress}%;"></div>
	</div>
</div>

<style>
	.matafome-toast {
		position: fixed;
		top: 48px; /* Fica logo abaixo da Titlebar (36px) com respiro elegante */
		right: 20px;
		z-index: 99999;
		min-width: 320px;
		max-width: 440px;
		background: rgba(22, 27, 34, 0.96);
		backdrop-filter: blur(14px);
		-webkit-backdrop-filter: blur(14px);
		border-radius: 10px;
		box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08);
		overflow: hidden;
		display: flex;
		flex-direction: column;
		animation: toastEnter 0.25s cubic-bezier(0.16, 1, 0.3, 1);
		user-select: none;
	}

	@keyframes toastEnter {
		from {
			opacity: 0;
			transform: translateY(-14px) scale(0.97);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	.toast-body {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 12px 14px;
	}

	.toast-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		margin-top: 1px;
	}

	.toast-content {
		flex: 1;
		font-size: 13px;
		line-height: 1.45;
		color: #E2E8F0;
		word-break: break-word;
	}

	.toast-msg {
		font-weight: 500;
	}

	.toast-close {
		background: none;
		border: none;
		color: rgba(255, 255, 255, 0.4);
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.15s;
		flex-shrink: 0;
	}

	.toast-close:hover {
		color: #FFFFFF;
		background: rgba(255, 255, 255, 0.1);
	}

	.toast-progress-track {
		height: 3px;
		width: 100%;
		background: rgba(255, 255, 255, 0.06);
		overflow: hidden;
	}

	.toast-progress-bar {
		height: 100%;
		transition: width 0.1s linear;
	}

	/* Cores temáticas elegantes */
	.success {
		border-left: 4px solid #10B981;
	}
	.success .toast-icon {
		color: #10B981;
	}
	.success .toast-progress-bar {
		background: #10B981;
	}

	.warning {
		border-left: 4px solid #F59E0B;
	}
	.warning .toast-icon {
		color: #F59E0B;
	}
	.warning .toast-progress-bar {
		background: #F59E0B;
	}

	.error, .danger {
		border-left: 4px solid #EF4444;
	}
	.error .toast-icon, .danger .toast-icon {
		color: #EF4444;
	}
	.error .toast-progress-bar, .danger .toast-progress-bar {
		background: #EF4444;
	}

	.info, .primary {
		border-left: 4px solid #3B82F6;
	}
	.info .toast-icon, .primary .toast-icon {
		color: #3B82F6;
	}
	.info .toast-progress-bar, .primary .toast-progress-bar {
		background: #3B82F6;
	}
</style>
